import { useState, useEffect } from "react";
import { useDrive } from "../App";
import { Sidebar } from "./Dashboard";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calculator, Download, Receipt, TrendingUp, TrendingDown, Settings, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function VATReports() {
  const { initialized, folderIds, listFiles, getFileAsJson, uploadFile } = useDrive();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vatEnabled, setVatEnabled] = useState(null); // null = loading, true/false = loaded
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Load VAT setting from config
  useEffect(() => {
    if (initialized && folderIds.config) {
      loadVatSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, folderIds.config]);

  const loadVatSetting = async () => {
    try {
      const files = await listFiles(folderIds.config);
      const configFile = files.find(f => f.name === 'config.json');
      if (configFile) {
        const config = await getFileAsJson(configFile.id);
        setVatEnabled(config.business?.vatEnabled || false);
      } else {
        setVatEnabled(false);
      }
    } catch (e) {
      console.log("No config found, VAT disabled by default");
      setVatEnabled(false);
    }
  };

  useEffect(() => {
    if (initialized && folderIds.receipts && vatEnabled === true) {
      loadVATSummary();
    } else if (vatEnabled === false) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, folderIds, selectedMonth, vatEnabled]);

  const loadVATSummary = async () => {
    setLoading(true);
    try {
      const receipts = [];
      const monthFolders = await listFiles(folderIds.receipts, 'application/vnd.google-apps.folder');
      const targetFolder = monthFolders.find(f => f.name === selectedMonth);
      
      if (targetFolder) {
        const files = await listFiles(targetFolder.id);
        const jsonFiles = files.filter(f => f.name.endsWith('.json'));
        
        for (const jsonFile of jsonFiles) {
          try {
            const receipt = await getFileAsJson(jsonFile.id);
            receipts.push(receipt);
          } catch (e) {}
        }
      }
      
      // Calculate summary
      const byRate = {
        "20%": { vat: 0, net: 0, gross: 0, count: 0 },
        "5%": { vat: 0, net: 0, gross: 0, count: 0 },
        "0%": { vat: 0, net: 0, gross: 0, count: 0 },
        "exempt": { vat: 0, net: 0, gross: 0, count: 0 }
      };
      
      let totalVat = 0, totalNet = 0, totalGross = 0;
      
      for (const r of receipts) {
        const vat = r.vatAmount || 0;
        const total = r.totalAmount || 0;
        const net = total - vat;
        
        totalVat += vat;
        totalNet += net;
        totalGross += total;
        
        const rateKey = r.vatRate === 20 ? "20%" : r.vatRate === 5 ? "5%" : r.vatRate === 0 ? "0%" : "exempt";
        byRate[rateKey].vat += vat;
        byRate[rateKey].net += net;
        byRate[rateKey].gross += total;
        byRate[rateKey].count += 1;
      }
      
      setSummary({
        period: selectedMonth,
        totalVatPaid: totalVat,
        netSpend: totalNet,
        grossSpend: totalGross,
        receiptsCount: receipts.length,
        byRate
      });
      
    } catch (error) {
      console.error("Error loading VAT summary:", error);
      toast.error("Failed to load VAT summary");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!summary) return;
    
    const rows = [
      ["VAT Summary Report", selectedMonth],
      [""],
      ["Total VAT Paid", `£${summary.totalVatPaid.toFixed(2)}`],
      ["Net Spend", `£${summary.netSpend.toFixed(2)}`],
      ["Gross Spend", `£${summary.grossSpend.toFixed(2)}`],
      ["Total Receipts", summary.receiptsCount],
      [""],
      ["Rate", "Count", "Net", "VAT", "Gross"],
      ...Object.entries(summary.byRate).map(([rate, data]) => [
        rate, data.count, `£${data.net.toFixed(2)}`, `£${data.vat.toFixed(2)}`, `£${data.gross.toFixed(2)}`
      ])
    ];
    
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vat_summary_${selectedMonth}.csv`;
    a.click();
    
    toast.success("VAT report exported");
  };

  // Generate month options
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  const vatRates = [
    { key: "20%", label: "Standard Rate (20%)", color: "bg-blue-100 text-blue-700" },
    { key: "5%", label: "Reduced Rate (5%)", color: "bg-emerald-100 text-emerald-700" },
    { key: "0%", label: "Zero Rate (0%)", color: "bg-slate-100 text-slate-700" },
    { key: "exempt", label: "Exempt", color: "bg-amber-100 text-amber-700" }
  ];

  if (!initialized) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="vat-reports" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  // Show message when VAT is disabled
  if (vatEnabled === false) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="vat-reports" />
        <main className="lg:ml-64 pt-16 lg:pt-0">
          <div className="p-6 md:p-8 lg:p-12 max-w-4xl">
            <div className="mb-8">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                VAT Reports
              </h1>
              <p className="text-slate-600 mt-1">Monthly VAT summaries</p>
            </div>

            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-amber-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">VAT Tracking Not Enabled</h2>
                  <p className="text-slate-600 mb-6 max-w-md">
                    VAT reports are only available for VAT-registered businesses. If your business is registered for VAT in the UK, enable VAT tracking in Settings.
                  </p>
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6 text-left max-w-md">
                    <Calculator className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800">Not VAT Registered?</p>
                      <p className="text-sm text-blue-700">
                        If you're not VAT registered, you don't need to track VAT. You can continue using the Receipts and Reports features for income and expense tracking.
                      </p>
                    </div>
                  </div>
                  <Button data-testid="go-to-settings-btn" onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Go to Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // Loading state for VAT setting
  if (vatEnabled === null) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="vat-reports" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeItem="vat-reports" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                VAT Reports
              </h1>
              <p className="text-slate-600 mt-1">Monthly VAT summaries</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleExportCSV} disabled={loading || !summary}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : summary && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Total VAT Paid</p>
                        <p className="text-3xl font-bold text-emerald-600">£{summary.totalVatPaid.toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Calculator className="w-6 h-6 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Net Spend</p>
                        <p className="text-3xl font-bold text-slate-900">£{summary.netSpend.toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <TrendingDown className="w-6 h-6 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Gross Spend</p>
                        <p className="text-3xl font-bold text-slate-900">£{summary.grossSpend.toFixed(2)}</p>
                      </div>
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500 mb-1">Receipts</p>
                        <p className="text-3xl font-bold text-slate-900">{summary.receiptsCount}</p>
                      </div>
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <Receipt className="w-6 h-6 text-slate-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-semibold text-slate-900">VAT Breakdown by Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                          <th className="py-3 px-4 text-left font-medium">Rate</th>
                          <th className="py-3 px-4 text-right font-medium">Receipts</th>
                          <th className="py-3 px-4 text-right font-medium">Net Amount</th>
                          <th className="py-3 px-4 text-right font-medium">VAT Amount</th>
                          <th className="py-3 px-4 text-right font-medium">Gross Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vatRates.map((rate) => {
                          const data = summary.byRate[rate.key];
                          return (
                            <tr key={rate.key} className="border-b border-slate-100">
                              <td className="py-4 px-4">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${rate.color}`}>
                                  {rate.label}
                                </span>
                              </td>
                              <td className="py-4 px-4 text-right text-slate-900 font-medium">{data.count}</td>
                              <td className="py-4 px-4 text-right text-slate-900 font-mono">£{data.net.toFixed(2)}</td>
                              <td className="py-4 px-4 text-right text-emerald-600 font-mono font-medium">£{data.vat.toFixed(2)}</td>
                              <td className="py-4 px-4 text-right text-slate-900 font-mono">£{data.gross.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-50 font-semibold">
                          <td className="py-4 px-4 text-slate-900">Total</td>
                          <td className="py-4 px-4 text-right text-slate-900">{summary.receiptsCount}</td>
                          <td className="py-4 px-4 text-right text-slate-900 font-mono">£{summary.netSpend.toFixed(2)}</td>
                          <td className="py-4 px-4 text-right text-emerald-600 font-mono">£{summary.totalVatPaid.toFixed(2)}</td>
                          <td className="py-4 px-4 text-right text-slate-900 font-mono">£{summary.grossSpend.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
